import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { History } from 'history';
import { Button } from '../../../../components/Button/Button';
import { Loader } from '../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { PROFILE_TABS_URLS } from '../../../../consts/profile';
import { verifyEmail, verifyPhone } from 'Src/helpers/service/nobody/confirm';
import { changeEmail, changePhone, resendEmailCode, resendPhoneCode } from 'Src/helpers/service/admin/user';
import { getUKPhoneWithoutFirstZero } from '../../../../helpers/phone/phone';
import { AppUser } from 'Src/views/App/App';
import { EmailChangeForm } from './EmailChangeForm/EmailChangeForm';
import { PhoneChangeForm } from './PhoneChangeForm/PhoneChangeForm';
import { VerificationForm } from 'Src/views/Registration/VerificationForm/VerificationForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import './VerificationTab.scss';

interface Props {
  user: AppUser;
  updateProfile: (profile) => void;
  history: History;
}

type MaybeBool = boolean | undefined;

interface State {
  isPhoneVerified: MaybeBool;
  isEmailVerified: MaybeBool;

  isEmailChangeModalOpen: boolean;
  isPhoneChangeModalOpen: boolean;
  isSuccessEmailChangeModalOpen: boolean;
  isSuccessPhoneChangeModalOpen: boolean;
  isResendEmailCodeModalOpen: boolean;
  isResendPhoneCodeModalOpen: boolean;

  isLoading: boolean;

  emailUpdated: string;
  phoneUpdated: string;

  emailCooldownUntilMs?: number;
  emailCooldownCode?: string;
  phoneCooldownUntilMs?: number;
  phoneCooldownCode?: string;
  emailResendError?: string;
  phoneResendError?: string;

  emailVerifyCooldownUntilMs?: number;
  emailVerifyCooldownCode?: string;
  phoneVerifyCooldownUntilMs?: number;
  phoneVerifyCooldownCode?: string;
  emailVerifyError?: string;
  phoneVerifyError?: string;

  emailUpdateCooldownUntilMs?: number;
  emailUpdateCooldownCode?: string;
  phoneUpdateCooldownUntilMs?: number;
  phoneUpdateCooldownCode?: string;
  emailActionError?: string;
  phoneActionError?: string;

  cooldownTick?: number;
}

const ModalTitle = (props: { icon: any; title: string; tone?: 'success' | 'warning' | 'info' }) => {
  const { icon, title, tone = 'info' } = props;
  return (
    <div className={`eModalTitleRow m${tone}`}>
      <span className="eModalTitleIcon">
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="eModalTitleText">{title}</span>
    </div>
  );
};

const ModalBody = (props: { lines: React.ReactNode[]; hint?: React.ReactNode }) => {
  const { lines, hint } = props;
  return (
    <div className="eModalBody">
      {lines.map((l, i) => (
        <div key={i} className="eModalBodyLine">
          {l}
        </div>
      ))}
      {hint && <div className="eModalBodyHint">{hint}</div>}
    </div>
  );
};

export class VerificationTab extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      emailUpdated: '',
      phoneUpdated: '',

      isPhoneVerified: undefined,
      isEmailVerified: undefined,

      isEmailChangeModalOpen: false,
      isPhoneChangeModalOpen: false,
      isSuccessEmailChangeModalOpen: false,
      isSuccessPhoneChangeModalOpen: false,
      isResendEmailCodeModalOpen: false,
      isResendPhoneCodeModalOpen: false,

      isLoading: false,

      emailCooldownUntilMs: undefined,
      emailCooldownCode: undefined,
      phoneCooldownUntilMs: undefined,
      phoneCooldownCode: undefined,

      emailVerifyCooldownUntilMs: undefined,
      emailVerifyCooldownCode: undefined,
      phoneVerifyCooldownUntilMs: undefined,
      phoneVerifyCooldownCode: undefined,

      emailResendError: undefined,
      phoneResendError: undefined,

      emailVerifyError: undefined,
      phoneVerifyError: undefined,

      emailUpdateCooldownUntilMs: undefined,
      emailUpdateCooldownCode: undefined,
      phoneUpdateCooldownUntilMs: undefined,
      phoneUpdateCooldownCode: undefined,

      emailActionError: undefined,
      phoneActionError: undefined,

      cooldownTick: undefined
    };
  }

  private cooldownTimer?: any;

  goToRequests = () => {
    this.props.history.push({ pathname: PROFILE_TABS_URLS.REQUESTS });
  };

  componentDidMount() {
    const { user } = this.props;

    this.setState({ isLoading: true });

    const isEmailVerified = !!propz.get(user, ['profile', 'verification', 'status', 'email']);
    const isPhoneVerified = !!propz.get(user, ['profile', 'verification', 'status', 'sms']);

    this.setState({
      isEmailVerified: isEmailVerified ? true : undefined,
      isPhoneVerified: isPhoneVerified ? true : undefined,
      isLoading: false
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const hadCooldown =
      !!prevState.emailCooldownUntilMs ||
      !!prevState.phoneCooldownUntilMs ||
      !!prevState.emailVerifyCooldownUntilMs ||
      !!prevState.phoneVerifyCooldownUntilMs ||
      !!prevState.emailUpdateCooldownUntilMs ||
      !!prevState.phoneUpdateCooldownUntilMs;

    const hasCooldown =
      !!this.state.emailCooldownUntilMs ||
      !!this.state.phoneCooldownUntilMs ||
      !!this.state.emailVerifyCooldownUntilMs ||
      !!this.state.phoneVerifyCooldownUntilMs ||
      !!this.state.emailUpdateCooldownUntilMs ||
      !!this.state.phoneUpdateCooldownUntilMs;

    if (!hadCooldown && hasCooldown) this.startCooldownTicker();
    if (hadCooldown && !hasCooldown) this.stopCooldownTicker();
  }

  private startCooldownTicker = () => {
    if (this.cooldownTimer) return;

    this.cooldownTimer = window.setInterval(() => {
      this.setState({ cooldownTick: Date.now() });
    }, 250);
  };

  private stopCooldownTicker = () => {
    if (this.cooldownTimer) {
      window.clearInterval(this.cooldownTimer);
      this.cooldownTimer = undefined;
    }
  };

  componentWillUnmount() {
    const { user, updateProfile } = this.props;
    const { isEmailVerified, isPhoneVerified, emailUpdated, phoneUpdated } = this.state;

    const profileUpdated = { ...user.profile };

    this.stopCooldownTicker();

    if (emailUpdated) propz.set(profileUpdated, ['email'], emailUpdated);
    if (phoneUpdated) propz.set(profileUpdated, ['phone'], phoneUpdated);

    if (isEmailVerified) propz.set(profileUpdated, ['verification', 'status', 'email'], true);
    if (isPhoneVerified) propz.set(profileUpdated, ['verification', 'status', 'sms'], true);

    updateProfile(profileUpdated);
  }

  private getRemainingSec = (untilMs?: number) => {
    if (!untilMs) return 0;
    const diffMs = untilMs - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  formatMmSs = (totalSec: number) => {
    const s = Math.max(0, Math.floor(totalSec));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    const ss2 = ss < 10 ? `0${ss}` : String(ss);
    return `${mm}:${ss2}`;
  };

  getEmailResendCooldownSecRemaining = () => this.getRemainingSec(this.state.emailCooldownUntilMs);
  getPhoneResendCooldownSecRemaining = () => this.getRemainingSec(this.state.phoneCooldownUntilMs);

  getEmailVerifyCooldownSecRemaining = () => this.getRemainingSec(this.state.emailVerifyCooldownUntilMs);
  getPhoneVerifyCooldownSecRemaining = () => this.getRemainingSec(this.state.phoneVerifyCooldownUntilMs);

  getEmailUpdateCooldownSecRemaining = () => this.getRemainingSec(this.state.emailUpdateCooldownUntilMs);
  getPhoneUpdateCooldownSecRemaining = () => this.getRemainingSec(this.state.phoneUpdateCooldownUntilMs);

  onEmailResendCooldownFinished = () => {
    this.setState({
      emailCooldownUntilMs: undefined,
      emailCooldownCode: undefined,
      emailResendError: undefined
    });
  };

  onPhoneResendCooldownFinished = () => {
    this.setState({
      phoneCooldownUntilMs: undefined,
      phoneCooldownCode: undefined,
      phoneResendError: undefined
    });
  };

  onEmailVerifyCooldownFinished = () => {
    this.setState({
      emailVerifyCooldownUntilMs: undefined,
      emailVerifyCooldownCode: undefined,
      emailVerifyError: undefined
    });
  };

  onPhoneVerifyCooldownFinished = () => {
    this.setState({
      phoneVerifyCooldownUntilMs: undefined,
      phoneVerifyCooldownCode: undefined,
      phoneVerifyError: undefined
    });
  };

  onEmailUpdateCooldownFinished = () => {
    this.setState({
      emailUpdateCooldownUntilMs: undefined,
      emailUpdateCooldownCode: undefined,
      emailActionError: undefined
    });
  };

  onPhoneUpdateCooldownFinished = () => {
    this.setState({
      phoneUpdateCooldownUntilMs: undefined,
      phoneUpdateCooldownCode: undefined,
      phoneActionError: undefined
    });
  };

  onResendEmailCodeClick = (event?: any) => {
    event?.preventDefault?.();

    if (this.getEmailResendCooldownSecRemaining() > 0) return;

    const { user } = this.props;

    this.setState({
      emailResendError: undefined,
      emailCooldownCode: undefined
    });

    resendEmailCode(user)
      .then((res: any) => {
        const nextResendAtMs = res?.nextResendAt ? new Date(res.nextResendAt).getTime() : undefined;

        this.setState({
          isResendEmailCodeModalOpen: true,
          emailCooldownUntilMs: nextResendAtMs ? nextResendAtMs + 1000 : undefined,
          emailCooldownCode: undefined,
          emailResendError: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot resend an email code right now. Please try again later.';

        this.setState({
          isResendEmailCodeModalOpen: false,
          emailResendError: text,
          emailCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          emailCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onResendPhoneCodeClick = (event?: any) => {
    event?.preventDefault?.();

    if (this.getPhoneResendCooldownSecRemaining() > 0) return;

    const { user } = this.props;

    this.setState({
      phoneResendError: undefined,
      phoneCooldownCode: undefined
    });

    resendPhoneCode(user)
      .then((res: any) => {
        const nextResendAtMs = res?.nextResendAt ? new Date(res.nextResendAt).getTime() : undefined;

        this.setState({
          isResendPhoneCodeModalOpen: true,
          phoneCooldownUntilMs: nextResendAtMs ? nextResendAtMs + 1000 : undefined,
          phoneCooldownCode: undefined,
          phoneResendError: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot resend an SMS code right now. Please try again later.';

        this.setState({
          isResendPhoneCodeModalOpen: false,
          phoneResendError: text,
          phoneCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          phoneCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onSubmitEmailCode = ({ emailCode }: { emailCode: string }) => {
    if (this.getEmailVerifyCooldownSecRemaining() > 0) return;

    const token = (emailCode || '').trim();
    if (!token) {
      this.setState({
        isEmailVerified: undefined,
        emailVerifyError: 'Please enter the email verification code.'
      });
      return;
    }

    const { user } = this.props;

    this.setState({
      isLoading: true,
      emailVerifyError: undefined,
      emailVerifyCooldownUntilMs: undefined,
      emailVerifyCooldownCode: undefined
    });

    verifyEmail(user, token)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          const text =
            propz.get(res, ['details', 'text']) || res.message || 'Too many attempts. Please try again later.';

          this.setState({
            isLoading: false,
            isEmailVerified: false,
            emailVerifyError: text,
            emailVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            emailVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        const confirmed = !!res?.confirmed;

        this.setState({
          isLoading: false,
          isEmailVerified: confirmed ? true : false,
          emailVerifyError: undefined,
          emailVerifyCooldownUntilMs: undefined,
          emailVerifyCooldownCode: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot verify your email right now. Please try again later.';

        this.setState({
          isLoading: false,
          isEmailVerified: false,
          emailVerifyError: text,
          emailVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          emailVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onSubmitPhoneCode = ({ phoneCode }: { phoneCode: string }) => {
    if (this.getPhoneVerifyCooldownSecRemaining() > 0) return;

    const token = (phoneCode || '').trim();
    if (!token) {
      this.setState({
        isPhoneVerified: undefined,
        phoneVerifyError: 'Please enter the phone verification code.'
      });
      return;
    }

    if (!this.state.isEmailVerified) {
      this.setState({
        isPhoneVerified: undefined,
        phoneVerifyError:
          'Please verify your email first. We’ll send your SMS code automatically once your email is confirmed.'
      });
      return;
    }

    const { user } = this.props;

    this.setState({
      isLoading: true,
      phoneVerifyError: undefined,
      phoneVerifyCooldownUntilMs: undefined,
      phoneVerifyCooldownCode: undefined
    });

    verifyPhone(user, token)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          const text =
            propz.get(res, ['details', 'text']) || res.message || 'Too many attempts. Please try again later.';

          this.setState({
            isLoading: false,
            isPhoneVerified: false,
            phoneVerifyError: text,
            phoneVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            phoneVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        const confirmed = !!res?.confirmed;

        this.setState({
          isLoading: false,
          isPhoneVerified: confirmed ? true : false,
          phoneVerifyError: undefined,
          phoneVerifyCooldownUntilMs: undefined,
          phoneVerifyCooldownCode: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot verify your phone number right now. Please try again later.';

        this.setState({
          isLoading: false,
          isPhoneVerified: false,
          phoneVerifyError: text,
          phoneVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          phoneVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onChangeEmailClick = (event?: any) => {
    event?.preventDefault?.();
    this.setState({
      isEmailChangeModalOpen: true,
      emailActionError: undefined
    });
  };

  onChangePhoneClick = (event?: any) => {
    event?.preventDefault?.();

    if (!this.state.isEmailVerified) {
      this.setState({
        phoneVerifyError:
          'Please verify your email first. You’ll be able to change your phone number after your email is confirmed.'
      });
      return;
    }

    this.setState({
      isPhoneChangeModalOpen: true,
      phoneActionError: undefined
    });
  };

  onEmailChangeFormCancelClick = () => this.setState({ isEmailChangeModalOpen: false });
  onPhoneChangeFormCancelClick = () => this.setState({ isPhoneChangeModalOpen: false });

  onEmailChangeFormSubmit = (data: any) => {
    if (this.getEmailUpdateCooldownSecRemaining() > 0) return;

    const { user } = this.props;

    this.setState({
      isLoading: true,
      emailActionError: undefined
    });

    changeEmail(user, data.email, data.captchaToken)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            isLoading: false,
            emailActionError: res.message || 'We cannot update your email right now. Please try again later.',
            emailUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            emailUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        this.setState({
          isLoading: false,
          isEmailChangeModalOpen: false,
          isSuccessEmailChangeModalOpen: true,
          emailUpdated: data.email,
          emailUpdateCooldownUntilMs: undefined,
          emailUpdateCooldownCode: undefined,
          emailActionError: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot update your email right now. Please try again later.';

        this.setState({
          isLoading: false,
          emailActionError: text,
          emailUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          emailUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onPhoneChangeFormSubmit = (data: any) => {
    if (this.getPhoneUpdateCooldownSecRemaining() > 0) return;

    const { user } = this.props;

    const { phone, phoneCode } = data;
    const phoneWithoutFirstZero = getUKPhoneWithoutFirstZero(phone, phoneCode);
    const phoneToPost = `${phoneCode}${phoneWithoutFirstZero}`;

    this.setState({
      isLoading: true,
      phoneActionError: undefined
    });

    changePhone(user, phoneToPost, data.captchaToken)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            isLoading: false,
            phoneActionError: res.message || 'We cannot update your phone right now. Please try again later.',
            phoneUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            phoneUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        this.setState({
          isLoading: false,
          isPhoneChangeModalOpen: false,
          isSuccessPhoneChangeModalOpen: true,
          phoneUpdated: phoneToPost,
          phoneUpdateCooldownUntilMs: undefined,
          phoneUpdateCooldownCode: undefined,
          phoneActionError: undefined
        });
      })
      .catch((e: any) => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        const text =
          propz.get(payload, ['details', 'text']) ||
          propz.get(payload, ['message']) ||
          'We cannot update your phone right now. Please try again later.';

        this.setState({
          isLoading: false,
          phoneActionError: text,
          phoneUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          phoneUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  closeSuccessEmailChangeModal = () => this.setState({ isSuccessEmailChangeModalOpen: false });
  closeSuccessPhoneChangeModal = () => this.setState({ isSuccessPhoneChangeModalOpen: false });
  closeResendEmailCodeModal = () => this.setState({ isResendEmailCodeModalOpen: false });
  closeResendPhoneCodeModal = () => this.setState({ isResendPhoneCodeModalOpen: false });

  renderEmailChangeModal() {
    const { isEmailChangeModalOpen } = this.state;
    const email = this.state.emailUpdated || propz.get(this.props.user, ['profile', 'email']);

    return (
      <SimpleModal isOpen={isEmailChangeModalOpen} customClass="eChangeContactModal">
        <EmailChangeForm
          onCancel={this.onEmailChangeFormCancelClick}
          onSubmit={this.onEmailChangeFormSubmit}
          email={email}
          cooldownSec={this.getEmailUpdateCooldownSecRemaining()}
          formatMmSs={this.formatMmSs}
          actionError={this.state.emailActionError}
          cooldownCode={this.state.emailUpdateCooldownCode}
          onCooldownFinished={this.onEmailUpdateCooldownFinished}
        />
      </SimpleModal>
    );
  }

  renderPhoneChangeModal() {
    const { isPhoneChangeModalOpen } = this.state;
    const phone = this.state.phoneUpdated || propz.get(this.props.user, ['profile', 'phone']);

    return (
      <SimpleModal isOpen={isPhoneChangeModalOpen} customClass="eChangeContactModal">
        <PhoneChangeForm
          onCancel={this.onPhoneChangeFormCancelClick}
          onSubmit={this.onPhoneChangeFormSubmit}
          phone={phone}
          cooldownSec={this.getPhoneUpdateCooldownSecRemaining()}
          formatMmSs={this.formatMmSs}
          actionError={this.state.phoneActionError}
          cooldownCode={this.state.phoneUpdateCooldownCode}
          onCooldownFinished={this.onPhoneUpdateCooldownFinished}
        />
      </SimpleModal>
    );
  }

  renderSuccessEmailChangeModal() {
    const { isSuccessEmailChangeModalOpen, emailUpdated } = this.state;
    const email = emailUpdated || propz.get(this.props.user, ['profile', 'email']);

    return (
      <SimpleModal
        isOpen={isSuccessEmailChangeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Email updated" tone="success" />}
        body={
          <ModalBody
            lines={[<>We’ve sent a new verification code to your updated email address.</>]}
            hint={
              <>
                Sent to <strong>{email}</strong>
              </>
            }
          />
        }
        buttonCancelText={'Ok'}
        onCloseClick={this.closeSuccessEmailChangeModal}
        customClass={'eVerificationTabModal enhanced-modal eUnifiedModal'}
      />
    );
  }

  renderSuccessPhoneChangeModal() {
    const { isSuccessPhoneChangeModalOpen, phoneUpdated } = this.state;
    const phone = phoneUpdated || propz.get(this.props.user, ['profile', 'phone']);

    return (
      <SimpleModal
        isOpen={isSuccessPhoneChangeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Phone updated" tone="success" />}
        body={
          <ModalBody
            lines={[<>We’ve sent a new verification code to your updated phone number.</>]}
            hint={
              <>
                Sent to <strong>{phone}</strong>
              </>
            }
          />
        }
        buttonCancelText={'Ok'}
        onCloseClick={this.closeSuccessPhoneChangeModal}
        customClass={'eVerificationTabModal enhanced-modal eUnifiedModal'}
      />
    );
  }

  renderResendEmailCodeModal() {
    const { isResendEmailCodeModalOpen } = this.state;
    const email = this.state.emailUpdated || propz.get(this.props.user, ['profile', 'email']);

    return (
      <SimpleModal
        isOpen={isResendEmailCodeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Verification code sent" tone="success" />}
        body={
          <ModalBody
            lines={[<>We have resent your email verification code.</>, <>Please check your inbox and spam folder.</>]}
            hint={
              <>
                Sent to <strong>{email}</strong>
              </>
            }
          />
        }
        buttonCancelText={'Got it'}
        onCloseClick={this.closeResendEmailCodeModal}
        customClass={'eChangeContactModal enhanced-modal eUnifiedModal'}
      />
    );
  }

  renderResendPhoneCodeModal() {
    const { isResendPhoneCodeModalOpen } = this.state;
    const phone = this.state.phoneUpdated || propz.get(this.props.user, ['profile', 'phone']);

    return (
      <SimpleModal
        isOpen={isResendPhoneCodeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Verification code sent" tone="success" />}
        body={
          <ModalBody
            lines={[
              <>We have resent your phone verification code.</>,
              <>If you don’t receive it, please double-check your phone number.</>
            ]}
            hint={
              <>
                Sent to <strong>{phone}</strong>
              </>
            }
          />
        }
        buttonCancelText={'Got it'}
        onCloseClick={this.closeResendPhoneCodeModal}
        customClass={'eChangeContactModal enhanced-modal eUnifiedModal'}
      />
    );
  }

  render() {
    const {
      isLoading,
      isEmailChangeModalOpen,
      isPhoneChangeModalOpen,
      isResendEmailCodeModalOpen,
      isResendPhoneCodeModalOpen,
      isSuccessEmailChangeModalOpen,
      isSuccessPhoneChangeModalOpen
    } = this.state;

    const profileEmail = propz.get(this.props.user, ['profile', 'email']);
    const profilePhone = propz.get(this.props.user, ['profile', 'phone']);

    const email = this.state.emailUpdated || profileEmail;
    const phone = this.state.phoneUpdated || profilePhone;

    {
      isLoading && (
        <div className="eRegistrationLoadingOverlay">
          <Loader />
        </div>
      );
    }

    const classes =
      isEmailChangeModalOpen ||
      isPhoneChangeModalOpen ||
      isResendEmailCodeModalOpen ||
      isResendPhoneCodeModalOpen ||
      isSuccessEmailChangeModalOpen ||
      isSuccessPhoneChangeModalOpen
        ? 'mt-3 modal-open'
        : 'mt-3';

    const allVerified = this.state.isEmailVerified === true && this.state.isPhoneVerified === true;

    return (
      <div className={classes}>
        {this.renderEmailChangeModal()}
        {this.renderPhoneChangeModal()}
        {this.renderResendEmailCodeModal()}
        {this.renderResendPhoneCodeModal()}
        {this.renderSuccessEmailChangeModal()}
        {this.renderSuccessPhoneChangeModal()}

        <div className="eVerificationTabForm">
          {allVerified && (
            <div className="eVerificationCongrats mt-3">
              <div className="eVerificationCongratsTitle">All set 🎉</div>
              <div className="eVerificationCongratsText">
                Your email and phone number are verified. Next step: request a role in your school to access features.
              </div>
              <div className="eVerificationCongratsActions mt-2">
                <Button onClick={this.goToRequests} text="Go to Requests" customClass="btn-primary" />
              </div>
            </div>
          )}
          <VerificationForm
            onSubmit={() => {}}
            onCancel={() => {}}
            email={email}
            phone={phone}
            onSendEmailCodeAgainClick={this.onResendEmailCodeClick}
            onChangeEmailClick={this.onChangeEmailClick}
            onSendPhoneCodeAgainClick={this.onResendPhoneCodeClick}
            onChangePhoneClick={this.onChangePhoneClick}
            isEmailConfirmed={this.state.isEmailVerified}
            isPhoneConfirmed={this.state.isPhoneVerified}
            onSubmitEmailClick={this.onSubmitEmailCode}
            onSubmitPhoneClick={this.onSubmitPhoneCode}
            emailResendError={this.state.emailResendError}
            emailVerifyError={this.state.emailVerifyError}
            phoneResendError={this.state.phoneResendError}
            phoneVerifyError={this.state.phoneVerifyError}
            emailCooldownSec={this.getEmailResendCooldownSecRemaining()}
            phoneCooldownSec={this.getPhoneResendCooldownSecRemaining()}
            emailCooldownCode={this.state.emailCooldownCode}
            phoneCooldownCode={this.state.phoneCooldownCode}
            emailVerifyCooldownSec={this.getEmailVerifyCooldownSecRemaining()}
            phoneVerifyCooldownSec={this.getPhoneVerifyCooldownSecRemaining()}
            emailVerifyCooldownCode={this.state.emailVerifyCooldownCode}
            phoneVerifyCooldownCode={this.state.phoneVerifyCooldownCode}
            formatMmSs={this.formatMmSs}
            onEmailResendCooldownFinished={this.onEmailResendCooldownFinished}
            onEmailVerifyCooldownFinished={this.onEmailVerifyCooldownFinished}
            onPhoneResendCooldownFinished={this.onPhoneResendCooldownFinished}
            onPhoneVerifyCooldownFinished={this.onPhoneVerifyCooldownFinished}
          />
        </div>
      </div>
    );
  }
}
